export type PluginsListState = {
    url: string;
    scrollTop: number;
    timestamp: number;
    layout?: string;
};

const PLUGINS_LIST_STATE_KEY = 'plugins:list:state';
const PLUGINS_LIST_STATE_TTL_MS = 30 * 60 * 1000;

const safeSessionStorage = () => {
    if (typeof window === 'undefined') return null;
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

export const normalizePluginsListUrl = (url: string): string => {
    if (!url) return '';
    return url.split('#')[0];
};

export const getPluginsListScrollContainer = (): HTMLElement | null => {
    if (typeof document === 'undefined') return null;
    const target = document.querySelector('#app-body');
    if (target instanceof HTMLElement && target.scrollHeight > target.clientHeight) {
        return target;
    }
    return null;
};

export const readPluginsListState = (): PluginsListState | null => {
    const storage = safeSessionStorage();
    if (!storage) return null;
    const raw = storage.getItem(PLUGINS_LIST_STATE_KEY);
    if (!raw) return null;

    try {
        const parsed = JSON.parse(raw) as PluginsListState;
        if (!parsed || typeof parsed.url !== 'string') {
            storage.removeItem(PLUGINS_LIST_STATE_KEY);
            return null;
        }
        if (typeof parsed.scrollTop !== 'number' || !Number.isFinite(parsed.scrollTop)) {
            storage.removeItem(PLUGINS_LIST_STATE_KEY);
            return null;
        }
        if (typeof parsed.timestamp !== 'number') {
            storage.removeItem(PLUGINS_LIST_STATE_KEY);
            return null;
        }
        if (Date.now() - parsed.timestamp > PLUGINS_LIST_STATE_TTL_MS) {
            storage.removeItem(PLUGINS_LIST_STATE_KEY);
            return null;
        }
        return parsed;
    } catch {
        storage.removeItem(PLUGINS_LIST_STATE_KEY);
        return null;
    }
};

export const writePluginsListState = (state: PluginsListState): void => {
    const storage = safeSessionStorage();
    if (!storage) return;
    try {
        storage.setItem(PLUGINS_LIST_STATE_KEY, JSON.stringify(state));
    } catch {
        // Ignore storage errors.
    }
};

export const clearPluginsListState = (): void => {
    const storage = safeSessionStorage();
    if (!storage) return;
    try {
        storage.removeItem(PLUGINS_LIST_STATE_KEY);
    } catch {
        // Ignore storage errors.
    }
};
